<template>
    <div class="faq-page-content">
        <div class="content-menu">
            <ul>
                <li v-for="item in items" :key="item.key">
                    <a :href="`#${SECTION_ID_TAG}${item.key}`" @click="handleMenuClick(item.sections[0].key)">
                        {{ item.title }}
                    </a>
                    <ul v-if="item.sections?.length">
                        <li v-for="section in item.sections" :key="section.key">
                            <a :href="`#${SECTION_ID_TAG}${section.key}`"
                                :class="{ active: activeSection === section.key }"
                                @click="handleMenuClick(section.key)">
                                {{ section.title }}
                            </a>
                        </li>
                    </ul>
                </li>
            </ul>
        </div>
        <div class="content-items">
            <div v-for="item in items" :id="`${SECTION_ID_TAG}${item.key}`" :key="item.key">
                <h5 class="item-title">{{ item.title }}</h5>
                <Accordion type="multiple" v-model="openedItems" collapsible>
                    <AccordionItem v-for="section in item.sections" :key="section.key" :value="section.key"
                        :id="`${SECTION_ID_TAG}${section.key}`" :class="{ 'pt-4': !section.title }">
                        <AccordionTrigger v-if="section.title">
                            <span class="section-title">{{ section.title }}</span>
                        </AccordionTrigger>
                        <AccordionContent v-html="section.body"></AccordionContent>
                    </AccordionItem>
                </Accordion>
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, nextTick } from 'vue'
import type { IFaqContentProps } from './FaqContent.model'
import { Accordion, AccordionItem, AccordionContent, AccordionTrigger } from '@/components/ui/accordion'
import { SECTION_ID_TAG, TOP_OFFSET } from './FaqContent.constant';
import { useBreakpoints } from '@vueuse/core';

const breakpoints = useBreakpoints({
    mobile: 0,
    desktop: 768
});

const { items } = defineProps<IFaqContentProps>()

const activeSection = ref(items.at(0)?.sections?.at(0)?.key ?? null);
let sectionElements: HTMLElement[] = []

const isClickScrolling = ref(false);
let scrollTimeout: number;
const openedItems = ref<string | string[] | undefined>()

onMounted(() => {
    if (breakpoints.active().value === "desktop") {
        openedItems.value = items.flatMap(item =>
            item?.sections?.map(section => section.key) ?? []
        );
    }

    if (breakpoints.active().value === "mobile") {
        openedItems.value = items.flatMap(item =>
            item?.sections
                ?.filter(section => !section.title)
                .map(section => section.key)
            ?? []
        );
    }
})

const updateActiveSection = () => {
    if (isClickScrolling.value) {
        return;
    }

    const scrollPosition = window.scrollY + TOP_OFFSET;
    const isAtBottom = window.innerHeight + window.scrollY >= document.body.scrollHeight - 2;

    if (isAtBottom) {
        const lastSection = sectionElements.at(-1);
        if (lastSection) {
            activeSection.value = lastSection.id.replace(SECTION_ID_TAG, '');
        }
        return;
    }

    if (!sectionElements.length) {
        return;
    }

    const closestSection = sectionElements.reduce(
        (prev, curr) => {
            const prevDistance = Math.abs(prev.offsetTop - scrollPosition);
            const currDistance = Math.abs(curr.offsetTop - scrollPosition);
            return currDistance < prevDistance ? curr : prev;
        }
    );

    if (closestSection) {
        activeSection.value = closestSection.dataset.sectionKey ?? null;
    }
};

const handleMenuClick = (key: string) => {
    activeSection.value = key;
    isClickScrolling.value = true;
    clearTimeout(scrollTimeout);
    scrollTimeout = window.setTimeout(() => {
        isClickScrolling.value = false;
    }, 1000);
}

onMounted(async () => {
    await nextTick();
    sectionElements = Array.from(document.querySelectorAll('[data-section-key]'))
    updateActiveSection();
    window.addEventListener('scroll', updateActiveSection)
    window.addEventListener('resize', updateActiveSection);
})

onBeforeUnmount(() => {
    window.removeEventListener('scroll', updateActiveSection)
    window.removeEventListener('resize', updateActiveSection);
    clearTimeout(scrollTimeout);
})
</script>
<style lang="scss" scoped>
:global(html) {
    scroll-behavior: smooth;
}

.faq-page-content {
    display: grid;
    grid-template-columns: 1fr;
    gap: 20px;
}

.active {
    color: var(--primary);
    font-weight: 800;
}

.content-items {
    :deep(a) {
        color: var(--primary)
    }

    .item-title {
        color: var(--primary);
        font-size: 24px;
        font-weight: 700;
        line-height: 34px;
    }

    .section-title {
        text-transform: uppercase;
        font-size: 18px;
        font-weight: 600;
        line-height: 26px;
    }
}

.content-menu {
    max-height: fit-content;


    &>ul>li {
        &:has(ul li a.active)>a {
            color: var(--primary);
            font-weight: 800;
        }
    }

    & ul {
        list-style: none;
        padding-left: 0;

        ul {
            padding-left: 15px;
        }
    }
}

.content-items {
    [id^="section_"]:target {
        animation: flash-highlight 1.5s ease-out;
    }
}


@media (min-width: 768px) {
    .faq-page-content {
        display: grid;
        grid-template-columns: 1fr 3fr;
        gap: 20px;
    }

    .content-menu {
        position: sticky;
        top: 15px;
    }
}

@keyframes flash-highlight {
    0% {
        background-color: #89c76633;
    }

    100% {
        background-color: transparent;
    }
}
</style>
